import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, catchError, interval, switchMap, take, throwError } from 'rxjs';
import { InAppNotification, InAppNotificationModel, InAppNotificationsLatestStatus, InAppNotificationsPayload } from '@shared/models/app-notifications.model';
import { MessageHandlerService } from './message-handler.service';
import { API_PREFIX } from '../util/constants';

const POLL_API_INTERVAL_IN_MLS = 300000;

const apiRoot = `api/${API_PREFIX.notification}/users/me/notifications`;
@Injectable({ providedIn: 'root' })
export class AppNotificationService {
  private notificationSubscription?: Subscription;
  public readonly isEnableNotificationBellIcon = new BehaviorSubject<boolean>(false);

  readonly isEnableNotificationBellIcon$ = this.isEnableNotificationBellIcon.asObservable();
  public readonly reloadNotifications = new BehaviorSubject<boolean>(false);

  readonly reloadNotifications$ = this.reloadNotifications.asObservable();
  public readonly lastSyncedTime = new BehaviorSubject<Date | null>(null);
  readonly lastSyncedTime$ = this.lastSyncedTime.asObservable();
  public constructor(private http: HttpClient, private msgService: MessageHandlerService) { }

  public getRecentNotifications(payload: InAppNotificationsPayload): Observable<InAppNotificationsLatestStatus> {
    return this.http.post<InAppNotificationsLatestStatus>(`${apiRoot}/recent`, payload);
  }

  public getInAppNotifications(payload: InAppNotificationsPayload): Observable<InAppNotificationModel> {
    return this.http.post<InAppNotificationModel>(`${apiRoot}`, payload);
  }

  public deleteAllInAppNotifications(payload: InAppNotificationsPayload) {
    return this.http.delete<void>(`${apiRoot}/bulk`, { body: payload }).pipe(
      catchError(err => {
        this.msgService.showMessage({ isError: true, content: 'Something went wrong' });
        this.reloadNotifications.next(false);
        return throwError(() => err);
      })
    ).subscribe(() => {
      this.msgService.showMessage({ content: 'All Notifications Deleted Successfully.' });
      this.reloadNotifications.next(true);
    });
  }

  public markAsReadAllInAppNotifications(payload: InAppNotificationsPayload) {
    return this.http.patch(`${apiRoot}`, payload).pipe(
      catchError(err => {
        this.msgService.showMessage({ isError: true, content: 'Something went wrong' });
        this.reloadNotifications.next(false);
        return throwError(() => err);
      })
    ).subscribe(() => {
      this.msgService.showMessage({ content: 'Notifications Mark all as read Successfully.' });
      this.reloadNotifications.next(true);
    });
  }

  public deleteAll(payload: InAppNotificationsPayload) {
    this.msgService.confirm('Are you sure you want to delete all Notifications?', 'Please confirm')
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed?: boolean) => {
        if (confirmed) {
          this.deleteAllInAppNotifications(payload);
        }
      });
  }
  public deleteNotification(notificationId: number) {
    this.msgService.confirm('Are you sure you want to delete this Notification?', 'Please confirm')
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed?: boolean) => {
        if (confirmed) {
          this.deleteInAppNotification(notificationId);
        }
      });
  }

  public deleteInAppNotification(notificationId: number) {
    return this.http.delete<InAppNotification>(`${apiRoot}/${notificationId}`).pipe(
      catchError(err => {
        this.msgService.showMessage({ isError: true, content: 'Something went wrong' });
        this.reloadNotifications.next(false);
        return throwError(() => err);
      }),
    ).subscribe(() => {
      this.msgService.showMessage({ content: 'Notification Deleted Successfully.' });
      this.reloadNotifications.next(true);
    }, (err) => {
      this.msgService.showMessage({ isError: true, content: 'Something went wrong' });
      this.reloadNotifications.next(false);
    });
  }

  public markasReadNotification(notificationId: number | null) {
    return this.http.patch(`${apiRoot}/${notificationId}/read`, {}).pipe(
      catchError(err => {
        this.msgService.showMessage({ isError: true, content: 'Something went wrong' });
        this.reloadNotifications.next(false);
        return throwError(() => err);
      })
    ).subscribe(() => {
      this.msgService.showMessage({ content: 'Notification Mark as read Successfully.' });
      this.reloadNotifications.next(true);
    });
  }

  public markasUnReadNotification(notificationId: number | null) {
    return this.http.patch(`${apiRoot}/${notificationId}/unread`, {}).pipe(
      catchError(err => {
        this.msgService.showMessage({ isError: true, content: 'Something went wrong' });
        this.reloadNotifications.next(false);
        return throwError(() => err);
      })
    ).subscribe(() => {
      this.msgService.showMessage({ content: 'Notification Mark as unread Successfully.' });
      this.reloadNotifications.next(true);
    });
  }

  public startPolling(notificationPayload: InAppNotificationsPayload) {
    return interval(POLL_API_INTERVAL_IN_MLS).pipe(
      switchMap(() => this.getRecentNotifications({
        ...notificationPayload,
        lastSyncedTime: this.lastSyncedTime.value
      }))
    );
  }
}

